import {
    Card,
    CardHeader,
    makeStyles,
    TableCellLayout,
    type TableColumnDefinition,
    Text,
} from '@fluentui/react-components';
import { memo, useMemo } from 'react';
import { graphql, usePreloadedQuery } from 'react-relay';
import { useMediaQuery } from 'usehooks-ts';

import { createTableColumns } from '../list/column/column-util';
import { QueryList } from '../list/QueryList';
import { ProcessDefinitionListLink } from '../process-definition/process-definition-list-link';

import type { processDefinitionsArchivedQuery } from './__generated__/processDefinitionsArchivedQuery.graphql';
import type { processDefinitionsArchivedQueryFragment$data } from './__generated__/processDefinitionsArchivedQueryFragment.graphql';

import { processDefinitionsArchivedRouteApi } from './process-definitions-archived-api';
import {
    ProcessDefinitionsArchivedQueryCtx,
    useProcessDefinitionsArchivedQueryFragmentRef,
} from './process-definitions-archived-query';

import { toolbarItems } from './process-definitions-archived-toolbar';
import { getStartWhenIcon } from '../process-definitions/process-definitions';

const columnSizingOptions = {
    startWhen: {
        minWidth: 24,
        defaultWidth: 24,
        idealWidth: 24,
        maxWidth: 24,
    },
};

export const ProcessDefinitionsArchived = memo(() => {
    const isTablet = useMediaQuery('(max-width: 1100px)');
    const isMobile = useMediaQuery('(max-width: 768px)');

    const processDefinitionsArchived = usePreloadedQuery<processDefinitionsArchivedQuery>(
        query,
        processDefinitionsArchivedRouteApi.useLoaderData()
    ).getProcessDefinitions;

    const queryFragment = useProcessDefinitionsArchivedQueryFragmentRef(processDefinitionsArchived);

    const items = queryFragment?.content ?? [];

    // 🔥 Tablet: weniger Spalten
    const responsiveColumns = useMemo(() => {
        if (isTablet) {
            return columns.filter(
                (c) => c.columnId !== 'definitionKey' && c.columnId !== 'definitionVersion'
            );
        }

        return columns;
    }, [isTablet]);

    return (
        <ProcessDefinitionsArchivedQueryCtx.Provider value={processDefinitionsArchived}>
            {isMobile ? (
                <ProcessDefinitionArchivedCards items={items} />
            ) : (
                <QueryList
                    columnSizingOptions={columnSizingOptions}
                    columns={responsiveColumns}
                    items={items}
                    pagination={queryFragment?.pageInfo}
                    routeApi={processDefinitionsArchivedRouteApi}
                    selectionMode="single"
                    toolbar={toolbarItems}
                />
            )}
        </ProcessDefinitionsArchivedQueryCtx.Provider>
    );
});
ProcessDefinitionsArchived.displayName = 'ProcessDefinitionsArchived';

const query = graphql`
    query processDefinitionsArchivedQuery(
        $pageNum: Int
        $pageSize: Int
        $sortBy: String
        $sortDir: SortDirection
        $search: String
        $searchBy: [String]
        $filter: ProcessDefinitionFilterInput
    ) {
        getProcessDefinitions(
            pageNum: $pageNum
            pageSize: $pageSize
            sortBy: $sortBy
            sortDir: $sortDir
            search: $search
            searchBy: $searchBy
            filterInput: $filter
        ) {
            ...processDefinitionsArchivedQueryFragment
        }
    }
`;

type TProcessDefinitionsArchived =
    processDefinitionsArchivedQueryFragment$data['content'][number][];

type TProcessDefinitionArchived = TProcessDefinitionsArchived[number];

const columns: TableColumnDefinition<TProcessDefinitionArchived>[] =
    createTableColumns<TProcessDefinitionArchived>(
        {
            columnId: 'startWhen',
            renderCell: (item) => getStartWhenIcon(item.startWhen),
            renderHeaderCell: '',
        },
        {
            columnId: 'id',
            renderCell: (item) => (
                <TableCellLayout>
                    <ProcessDefinitionListLink>{item}</ProcessDefinitionListLink>
                </TableCellLayout>
            ),
            renderHeaderCell: 'Prozessname',
        },
        {
            columnId: 'listName',
            renderHeaderCell: 'Liste',
        },
        {
            columnId: 'definitionVersion',
            renderHeaderCell: 'Version',
        },
        {
            columnId: 'definitionKey',
            renderHeaderCell: 'Prozess-ID',
        }
    );

const useCardStyles = makeStyles({
    container: {
        display: 'grid',
        gap: '1rem',
        padding: '1rem',
    },
    card: {
        padding: '1rem',
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
});

const ProcessDefinitionArchivedCards = ({
    items,
}: {
    items: readonly TProcessDefinitionArchived[];
}) => {
    const styles = useCardStyles();

    return (
        <div className={styles.container}>
            {items.map((item) => (
                <Card key={item.id} className={styles.card}>
                    <CardHeader
                        header={<ProcessDefinitionListLink>{item}</ProcessDefinitionListLink>}
                        description={`Liste: ${item.listName}`}
                    />

                    <div className={styles.row}>
                        <Text size={200}>Version {item.definitionVersion}</Text>
                        {getStartWhenIcon(item.startWhen)}
                    </div>
                </Card>
            ))}
        </div>
    );
};
